/**
 * Conference Coordination — the CLERK's workspace.
 *
 * The clerk coordinates: sees every conference at its stage, proposes slots,
 * finalises the barrister-approved slot, and cancels. The barrister only decides
 * (see ConferenceApprovals); the solicitor is informed. One glance tells the clerk
 * exactly what needs doing — no hunting.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Users, CalendarPlus, CheckCircle, XCircle, Clock, Note, ArrowRight, CircleNotch,
} from '@phosphor-icons/react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getApiErrorMessage } from '@/lib/api';
import {
  fetchConferences, proposeConferenceSlots, confirmConferenceSlot, cancelConference,
  createConferenceRequest,
  PURPOSE_LABELS, FORMAT_LABELS, type Conference, type ConferencePurpose, type ConferenceFormat,
} from '@/lib/conferencesApi';
import { fetchMatters } from '@/lib/mattersApi';
import { useProposedSlotClashes } from '@/lib/useBarristerBusy';
import { ProposedSlotClashWarning } from '@/components/shared/ProposedSlotClashWarning';
import { ScrollLock } from '@/components/shared/ScrollLock';

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// The clerk's pipeline buckets — the professional stages, in priority order.
type Bucket = 'needs_proposal' | 'awaiting_barrister' | 'awaiting_solicitor' | 'upcoming';
const BUCKET_META: Record<Bucket, { label: string; hint: string; tone: string }> = {
  needs_proposal:     { label: 'Awaiting proposal',            hint: 'Propose slot(s) to the barrister', tone: 'text-amber-700 dark:text-amber-300' },
  awaiting_barrister: { label: 'Awaiting barrister decision',  hint: 'With counsel to approve/decline',  tone: 'text-blue-700 dark:text-blue-300' },
  awaiting_solicitor: { label: 'Awaiting solicitor confirmation', hint: 'Barrister approved — finalise',  tone: 'text-emerald-700 dark:text-emerald-300' },
  upcoming:           { label: 'Confirmed',                    hint: 'In the diary',                     tone: 'text-foreground' },
};

function bucketOf(c: Conference): Bucket | null {
  switch (c.status) {
    case 'requested':
    case 'barrister_declined':
    case 'alternative_suggested':
      return 'needs_proposal';
    case 'awaiting_barrister':
      return 'awaiting_barrister';
    case 'barrister_approved':
      return 'awaiting_solicitor';
    case 'confirmed':
      return 'upcoming';
    default:
      return null; // completed / cancelled — not actionable here
  }
}

export function ConferenceCoordination() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['conferences', 'coordination'],
    queryFn: () => fetchConferences({ limit: 100 }),
    refetchInterval: 120_000,
  });
  const conferences = data?.conferences ?? [];

  const [proposeFor, setProposeFor] = useState<Conference | null>(null);
  const [finaliseFor, setFinaliseFor] = useState<Conference | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['conferences'] });
    qc.invalidateQueries({ queryKey: ['diary'] });
  };

  const buckets = useMemo(() => {
    const b: Record<Bucket, Conference[]> = { needs_proposal: [], awaiting_barrister: [], awaiting_solicitor: [], upcoming: [] };
    for (const c of conferences) {
      const k = bucketOf(c);
      if (k) b[k].push(c);
    }
    // upcoming: sort by scheduled date ascending; others oldest-first (need attention)
    b.upcoming.sort((x, y) => (x.scheduledAt ?? '').localeCompare(y.scheduledAt ?? ''));
    return b;
  }, [conferences]);

  const total = buckets.needs_proposal.length + buckets.awaiting_barrister.length + buckets.awaiting_solicitor.length;

  const cancelMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelConference(id, reason),
    onSuccess: () => { toast.success('Conference cancelled'); invalidate(); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not cancel')),
  });

  if (isLoading) {
    return (
      <Card><CardContent className="flex items-center justify-center py-10"><CircleNotch className="h-5 w-5 animate-spin text-brand-600 dark:text-brand-400" /></CardContent></Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            <CardTitle>Conference coordination</CardTitle>
            {total > 0 && <Badge variant="warning">{total} to action</Badge>}
          </div>
          <Button variant="primary" size="sm" leftIcon={<CalendarPlus className="h-3.5 w-3.5" />} onClick={() => setNewOpen(true)}>
            New request
          </Button>
        </div>
        <CardDescription>Solicitor asks for a conference → you propose slots → counsel decides → you confirm.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {(['needs_proposal', 'awaiting_barrister', 'awaiting_solicitor', 'upcoming'] as Bucket[]).map((bk) => {
          const rows = buckets[bk];
          if (rows.length === 0) return null;
          const meta = BUCKET_META[bk];
          return (
            <div key={bk}>
              <div className="mb-2 flex items-center gap-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${meta.tone}`}>{meta.label}</span>
                <span className="text-[10px] text-muted-foreground">· {meta.hint}</span>
                <Badge variant="default" className="text-[10px]">{rows.length}</Badge>
              </div>
              <div className="space-y-2">
                {rows.map((c) => (
                  <div key={c.id} className="rounded-lg border border-border px-3 py-2.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-foreground">
                          {c.case.caseNumber} · {c.case.caseName}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                          <span>{PURPOSE_LABELS[c.purpose]}</span>
                          <span>· {FORMAT_LABELS[c.format]}</span>
                          <span>· {c.durationMinutes} min</span>
                          {c.layClientAttending && <span>· client attending</span>}
                        </div>
                        {/* Stage-specific context */}
                        {bk === 'awaiting_barrister' && c.proposedDates && (
                          <div className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                            Proposed: {c.proposedDates.map(fmtDateTime).join('  ·  ')}
                          </div>
                        )}
                        {c.status === 'barrister_declined' && c.barristerResponseNote && (
                          <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">Counsel unavailable: {c.barristerResponseNote}</div>
                        )}
                        {c.status === 'alternative_suggested' && c.barristerSuggestedDates && (
                          <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                            Counsel suggested: {c.barristerSuggestedDates.map(fmtDateTime).join('  ·  ')}
                          </div>
                        )}
                        {bk === 'awaiting_solicitor' && c.barristerChosenDate && (
                          <div className="mt-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                            Counsel approved {fmtDateTime(c.barristerChosenDate)} — confirm with the solicitor
                          </div>
                        )}
                        {bk === 'upcoming' && c.scheduledAt && (
                          <div className="mt-1 text-xs font-medium text-foreground">
                            {fmtDateTime(c.scheduledAt)}{c.locationDetails ? ` · ${c.locationDetails}` : ''}
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        {(bk === 'needs_proposal') && (
                          <Button variant="primary" size="sm" leftIcon={<CalendarPlus className="h-3.5 w-3.5" />} onClick={() => setProposeFor(c)}>
                            {c.status === 'requested' ? 'Propose slots' : 'Re-propose'}
                          </Button>
                        )}
                        {bk === 'awaiting_barrister' && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> with counsel</span>
                        )}
                        {bk === 'awaiting_solicitor' && (
                          <Button variant="primary" size="sm" leftIcon={<CheckCircle className="h-3.5 w-3.5" />} onClick={() => setFinaliseFor(c)}>
                            Confirm &amp; book
                          </Button>
                        )}
                        {bk !== 'upcoming' && (
                          <button
                            className="text-xs text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                            onClick={() => {
                              const reason = window.prompt('Cancel this conference — reason?');
                              if (reason && reason.trim()) cancelMut.mutate({ id: c.id, reason: reason.trim() });
                            }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {total === 0 && buckets.upcoming.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">No conferences to coordinate right now.</p>
        )}
      </CardContent>

      {proposeFor && (
        <ProposeSlotsModal
          conference={proposeFor}
          onClose={() => setProposeFor(null)}
          onDone={() => { setProposeFor(null); invalidate(); }}
        />
      )}
      {finaliseFor && (
        <FinaliseModal
          conference={finaliseFor}
          onClose={() => setFinaliseFor(null)}
          onDone={() => { setFinaliseFor(null); invalidate(); }}
        />
      )}
      {newOpen && (
        <NewRequestModal
          onClose={() => setNewOpen(false)}
          onDone={(c) => { setNewOpen(false); invalidate(); setProposeFor(c); }}
        />
      )}
    </Card>
  );
}

// ─── Clerk opens a Conference Request (solicitor phoned) ─────
function NewRequestModal({ onClose, onDone }: { onClose: () => void; onDone: (c: Conference) => void }) {
  const { data: matters } = useQuery({ queryKey: ['matters-picker-conf'], queryFn: () => fetchMatters().then((r) => r.matters) });
  const [caseId, setCaseId] = useState('');
  const [purpose, setPurpose] = useState<ConferencePurpose>('case_prep');
  const [format, setFormat] = useState<ConferenceFormat>('in_chambers');
  const [duration, setDuration] = useState(60);
  const [clientAttending, setClientAttending] = useState(false);
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => createConferenceRequest({ caseId, purpose, format, durationMinutes: duration, layClientAttending: clientAttending, notes: notes || undefined }),
    onSuccess: (c) => { toast.success('Conference requested — now propose times to counsel'); onDone(c); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not create the request')),
  });

  return (
    <ModalShell title="New conference request" onClose={onClose}>
      <p className="text-xs text-muted-foreground">A solicitor needs a conference with counsel — record it, then propose times.</p>
      <label className="text-xs text-muted-foreground">Matter
        <select value={caseId} onChange={(e) => setCaseId(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-card px-3 text-sm">
          <option value="">Select matter…</option>
          {(matters ?? []).map((m) => <option key={m.id} value={m.id}>{m.number} · {m.name}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-muted-foreground">Purpose
          <select value={purpose} onChange={(e) => setPurpose(e.target.value as ConferencePurpose)} className="mt-1 h-9 w-full rounded-md border border-border bg-card px-3 text-sm">
            {(Object.keys(PURPOSE_LABELS) as ConferencePurpose[]).map((p) => <option key={p} value={p}>{PURPOSE_LABELS[p]}</option>)}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">Format
          <select value={format} onChange={(e) => setFormat(e.target.value as ConferenceFormat)} className="mt-1 h-9 w-full rounded-md border border-border bg-card px-3 text-sm">
            {(Object.keys(FORMAT_LABELS) as ConferenceFormat[]).map((f) => <option key={f} value={f}>{FORMAT_LABELS[f]}</option>)}
          </select>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-muted-foreground">Duration (min)
          <input type="number" min={15} max={480} value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="mt-1 h-9 w-full rounded-md border border-border bg-card px-3 text-sm" />
        </label>
        <label className="mt-5 flex items-center gap-2 text-xs text-foreground">
          <input type="checkbox" checked={clientAttending} onChange={(e) => setClientAttending(e.target.checked)} /> Lay client attending
        </label>
      </div>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="What's it about? (optional)" className="w-full rounded-md border border-border bg-card p-2 text-sm" />
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" disabled={!caseId || mut.isPending} leftIcon={mut.isPending ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />} onClick={() => mut.mutate()}>
          Create &amp; propose
        </Button>
      </div>
    </ModalShell>
  );
}

// ─── Clerk proposes candidate slots ──────────────────────────
function ProposeSlotsModal({ conference, onClose, onDone }: { conference: Conference; onClose: () => void; onDone: () => void }) {
  const seed = conference.barristerSuggestedDates?.[0]?.slice(0, 16) ?? '';
  const [slots, setSlots] = useState<string[]>([seed, '', '']);
  const [duration, setDuration] = useState(conference.durationMinutes);
  const [location, setLocation] = useState(conference.locationDetails ?? '');

  // Warn (never block) if a proposed slot clashes the assigned barrister's diary.
  const barristerUserId =
    conference.case.leadBarrister?.userId ?? conference.triggeringAllocation?.barrister?.user?.id ?? null;
  const isoSlots = slots.filter(Boolean).map((s) => new Date(s).toISOString());
  const clashes = useProposedSlotClashes(barristerUserId, isoSlots, duration);

  const mut = useMutation({
    mutationFn: () => proposeConferenceSlots(conference.id, {
      proposedDates: slots.filter(Boolean).map((s) => new Date(s).toISOString()),
      durationMinutes: duration,
      locationDetails: location || undefined,
    }),
    onSuccess: () => { toast.success('Slots proposed — counsel notified to decide'); onDone(); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not propose slots')),
  });

  const valid = slots.some(Boolean);
  return (
    <ModalShell title={`Propose conference slots — ${conference.case.caseNumber}`} onClose={onClose}>
      <p className="text-xs text-muted-foreground">Offer counsel one or more times. They approve, decline, or suggest another.</p>
      {slots.map((s, i) => (
        <input
          key={i}
          type="datetime-local"
          value={s}
          onChange={(e) => setSlots((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
          className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm"
        />
      ))}
      <ProposedSlotClashWarning clashes={clashes} />
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-muted-foreground">Duration (min)
          <input type="number" min={15} max={480} value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="mt-1 h-9 w-full rounded-md border border-border bg-card px-3 text-sm" />
        </label>
        <label className="text-xs text-muted-foreground">Location / video
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Room 2 / Teams" className="mt-1 h-9 w-full rounded-md border border-border bg-card px-3 text-sm" />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" disabled={!valid || mut.isPending} leftIcon={mut.isPending ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />} onClick={() => mut.mutate()}>
          Propose to counsel
        </Button>
      </div>
    </ModalShell>
  );
}

// ─── Clerk finalises the barrister-approved slot ─────────────
function FinaliseModal({ conference, onClose, onDone }: { conference: Conference; onClose: () => void; onDone: () => void }) {
  const [when, setWhen] = useState((conference.barristerChosenDate ?? '').slice(0, 16));
  const [location, setLocation] = useState(conference.locationDetails ?? '');

  const mut = useMutation({
    mutationFn: () => confirmConferenceSlot(conference.id, {
      selectedAt: new Date(when).toISOString(),
      location: location || undefined,
    }),
    onSuccess: () => { toast.success('Confirmed — in the diary; solicitor informed'); onDone(); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not confirm')),
  });

  return (
    <ModalShell title={`Confirm conference — ${conference.case.caseNumber}`} onClose={onClose}>
      <p className="text-xs text-muted-foreground">Counsel approved this time. Confirming books the diary and informs the solicitor.</p>
      <label className="text-xs text-muted-foreground">Date &amp; time
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-border bg-card px-3 text-sm" />
      </label>
      <label className="text-xs text-muted-foreground">Location / video
        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Room 2 / Teams link" className="mt-1 h-9 w-full rounded-md border border-border bg-card px-3 text-sm" />
      </label>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" disabled={!when || mut.isPending} leftIcon={mut.isPending ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />} onClick={() => mut.mutate()}>
          Confirm &amp; book
        </Button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground"><Note className="h-4 w-4 text-brand-600 dark:text-brand-400" /> {title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><XCircle className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}
